// Image Modal Component
// Full-screen image viewer with black background

import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ModalOverlay,
  ModalContent,
  CloseButton,
  ModalImage,
  ImageInfo,
  ImageUserInfo,
  ProfileIcon,
  ImageDescription,
  ImageLikes,
  UsernameText,
} from "../../../styles/SocialFeed/ImageModal.styles";

interface ImageModalProps {
  isOpen: boolean;
  image: {
    id: string;
    userId: string;
    username: string;
    avatar: string;
    imageUrl: string;
    // Optional for the same reason as ImageCard: real uploads carry no stored
    // dimensions, and the modal sizes to the viewport anyway.
    width?: number;
    height?: number;
    timestamp: Date;
    likes?: number;
    description?: string;
  } | null;
  onClose: () => void;
  onViewProfile: (userId: string) => void;
}

export const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  image,
  onClose,
  onViewProfile,
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!image) return null;

  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewProfile(image.userId);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <CloseButton onClick={onClose} aria-label="Close image">
            ×
          </CloseButton>
          <ModalContent
            as={motion.div}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalImage
              src={image.imageUrl}
              alt={image.description || `Image by ${image.username}`}
            />
            <ImageInfo>
              <ImageUserInfo>
                <ProfileIcon
                  onClick={handleProfileClick}
                  $avatarUrl={image.avatar}
                  title={`View ${image.username}'s profile`}
                />
                <div>
                  <UsernameText>{image.username}</UsernameText>
                  {image.description && (
                    <ImageDescription>{image.description}</ImageDescription>
                  )}
                </div>
              </ImageUserInfo>
              {image.likes !== undefined && (
                <ImageLikes>❤️ {image.likes}</ImageLikes>
              )}
            </ImageInfo>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

